import { useRouter } from 'next/router';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

interface GlobalAudioPlayerContextType {
  currentUrl: string | null;
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  play: (url: string) => void;
  pause: () => void;
  stop: () => void;
  seek: (time: number) => void;
  isCurrentPlaying: (url: string) => boolean;
}

const GlobalAudioPlayerContext = createContext<GlobalAudioPlayerContextType | undefined>(
  undefined,
);

export const GlobalAudioPlayerProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const router = useRouter();
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [currentUrl, setCurrentUrl] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  // Create single global audio element
  useEffect(() => {
    const audio = new Audio();
    audioRef.current = audio;

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
    };

    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };

    const handlePlay = () => {
      setIsPlaying(true);
    };

    const handlePause = () => {
      setIsPlaying(false);
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.pause();
      audio.src = '';
    };
  }, []);

  const stop = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setCurrentUrl(null);
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
  }, []);

  const play = useCallback(
    (url: string) => {
      if (!audioRef.current) return;

      // If playing the same audio, just resume
      if (currentUrl === url && audioRef.current.paused) {
        audioRef.current.play();
        return;
      }

      // If different audio, stop current and play new
      if (currentUrl !== url) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        audioRef.current.src = url;
        setCurrentUrl(url);
        setCurrentTime(0);
      }

      audioRef.current.play();
    },
    [currentUrl],
  );

  const pause = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
    }
  }, []);

  const seek = useCallback((time: number) => {
    if (audioRef.current) {
      audioRef.current.currentTime = time;
    }
  }, []);

  const isCurrentPlaying = useCallback(
    (url: string) => {
      return currentUrl === url && isPlaying;
    },
    [currentUrl, isPlaying],
  );

  // Stop audio on route change
  useEffect(() => {
    const handleRouteChange = () => {
      stop();
    };

    router.events.on('routeChangeStart', handleRouteChange);

    return () => {
      router.events.off('routeChangeStart', handleRouteChange);
    };
  }, [router.events, stop]);

  // Stop audio on unmount
  useEffect(() => {
    return () => {
      stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value: GlobalAudioPlayerContextType = {
    currentUrl,
    isPlaying,
    currentTime,
    duration,
    play,
    pause,
    stop,
    seek,
    isCurrentPlaying,
  };

  return (
    <GlobalAudioPlayerContext.Provider value={value}>
      {children}
    </GlobalAudioPlayerContext.Provider>
  );
};

export const useGlobalAudioPlayer = () => {
  const context = useContext(GlobalAudioPlayerContext);
  if (context === undefined) {
    throw new Error(
      'useGlobalAudioPlayer must be used within a GlobalAudioPlayerProvider',
    );
  }
  return context;
};
